// services/adminAttendanceService.js - Admin Attendance Management Service
import api from './api';

const adminAttendanceService = {
  // Dashboard and Overview
  getLiveAttendance: async () => {
    return await api.get('/admin/attendance/live');
  },

  getAttendanceDashboard: async () => {
    return await api.get('/admin/attendance/dashboard');
  },

  // Analytics and Reporting
  getAnalytics: async (params = {}) => {
    return await api.get('/admin/analytics/attendance', { params });
  },

  getProductivityAnalytics: async (params = {}) => {
    return await api.get('/admin/analytics/productivity', { params });
  },

  generateReport: async (params = {}) => {
    return await api.get('/admin/reports/attendance', { 
      params,
      responseType: params.format === 'xlsx' ? 'blob' : 'json'
    });
  },

  // Attendance Records Management
  getAttendanceRecords: async (params = {}) => {
    return await api.get('/admin/attendance', { params });
  },

  getAttendanceRecord: async (id) => {
    return await api.get(`/admin/attendance/${id}`);
  },

  updateAttendanceRecord: async (id, data) => {
    return await api.put(`/admin/attendance/${id}`, data);
  },

  deleteAttendanceRecord: async (id) => {
    return await api.delete(`/admin/attendance/${id}`);
  },

  // Manual Entries
  createManualEntry: async (data) => {
    return await api.post('/admin/attendance/manual-entry', data);
  },

  // Employee Management
  getEmployeeAttendanceSummary: async (userId, params = {}) => {
    return await api.get(`/admin/attendance/summary/${userId}`, { params });
  },

  getEmployeeList: async () => {
    return await api.get('/admin/employees');
  },

  // Anomaly Detection
  getAttendanceAnomalies: async (params = {}) => {
    return await api.get('/admin/attendance/anomalies', { params });
  },

  // Bulk Operations
  bulkApproveCorrections: async (recordIds) => {
    return await api.post('/admin/attendance/bulk-approve', { recordIds });
  },

  bulkDeleteRecords: async (recordIds) => {
    return await api.post('/admin/attendance/bulk-delete', { recordIds });
  },

  // Export Operations
  exportAttendanceData: async (params = {}) => {
    return await api.get('/admin/attendance/export', {
      params,
      responseType: 'blob'
    });
  },

  exportAnalytics: async (params = {}) => {
    return await api.get('/admin/analytics/export', {
      params,
      responseType: 'blob'
    });
  },

  // Field Work Management
  getFieldWorkRequests: async (params = {}) => {
    return await api.get('/admin/field-work/requests', { params });
  },

  approveFieldWork: async (recordId, approved = true) => {
    return await api.post(`/admin/field-work/${recordId}/approve`, { approved });
  },

  // Break Management (Admin oversight)
  getBreakAnalytics: async (params = {}) => {
    return await api.get('/admin/analytics/breaks', { params });
  },

  // Settings and Configuration
  getAttendanceSettings: async () => {
    return await api.get('/admin/settings/attendance');
  },

  updateAttendanceSettings: async (settings) => {
    return await api.put('/admin/settings/attendance', settings);
  },

  // Real-time Monitoring
  getActiveSessions: async () => {
    return await api.get('/admin/attendance/active-sessions');
  },

  getRecentActivity: async (params = {}) => {
    return await api.get('/admin/attendance/recent-activity', { params });
  },

  // Geofencing and Location Management
  getLocationAnalytics: async (params = {}) => {
    return await api.get('/admin/analytics/locations', { params });
  },

  // Compliance and Auditing
  getAuditLogs: async (params = {}) => {
    return await api.get('/admin/attendance/audit-logs', { params });
  },

  generateComplianceReport: async (params = {}) => {
    return await api.get('/admin/reports/compliance', { 
      params,
      responseType: 'blob'
    });
  },

  // Attendance Patterns
  getAttendancePatterns: async (params = {}) => {
    return await api.get('/admin/analytics/patterns', { params });
  },

  // Shift Management Integration
  getShiftCompliance: async (params = {}) => {
    return await api.get('/admin/attendance/shift-compliance', { params });
  },

  // Request approval / rejection
  approveRequest: async (id, adminNotes = '') => {
    return await api.post(`/admin/attendance/requests/${id}/approve`, { admin_notes: adminNotes });
  },

  rejectRequest: async (id, rejectionReason, adminNotes = '') => {
    return await api.post(`/admin/attendance/requests/${id}/reject`, { rejection_reason: rejectionReason, admin_notes: adminNotes });
  }
};

export default adminAttendanceService;